import { useMemo } from "react";

import styled from "styled-components";

import { CREATE_GROUP } from "Flocknote/GlobalComponents/Modal/contentKeys";
import { useModal } from "Flocknote/stateHooks";

import { useClearActiveAction } from "Dashboard/components/WhatsNext/whatsNextStore";
import {
  FloatingButton,
  InnerWrapper,
  iconPaddingRight,
} from "NavBar/components/FloatingButtonPanel/ButtonStyles";
import IconWrapper from "NavBar/components/FloatingButtonPanel/IconWrapper";
import { navSectionKeys, useNewButtonRef } from "NavBar/navBarStore";
import { useIsCreateGroupButtonFlashing } from "NavBar/stateHooks";

import FnrContainedIcon from "UI/FnrContainedIcon";
import FnrSimpleReveal from "UI/FnrSimpleReveal";
import FnrTypography from "UI/FnrTypography";
import { navAccentColors } from "UI/colors";

import { addOpacity } from "Utilities/Colors/colorUtils";
import {
  useIsFocusVisible,
  useIsHovered,
} from "Utilities/Hooks/useIsHoverFocused";

import { useShowButtonText } from "./floatingSearchStore";
import {
  activeBackgroundColor,
  inactiveBackgroundColor,
} from "./styleConstants";

const importGroupsManagement = () => import("GroupsManagement");

const NewButton = () => {
  const [{ isOpen, contentKey }, { openModal }] = useModal();
  const clearActiveAction = useClearActiveAction();
  const isCreateGroupButtonFlashing = useIsCreateGroupButtonFlashing();
  const newButtonRef = useNewButtonRef();

  const isHovered = Boolean(useIsHovered(newButtonRef));
  const isFocusVisible = Boolean(useIsFocusVisible(newButtonRef));
  const showButtonText = useShowButtonText();

  const isModalOpen = useMemo(
    () => isOpen && contentKey === CREATE_GROUP,
    [isOpen, contentKey]
  );

  const handleClick = () => {
    openModal(CREATE_GROUP);
    clearActiveAction();
  };

  return (
    <FloatingButton
      ref={newButtonRef}
      onClick={handleClick}
      onMouseEnter={importGroupsManagement}
      data-cy="NavBar_FloatingNewButton"
      $isSelected={isModalOpen}
      $glowColor={navAccentColors[navSectionKeys.groups][0]}
      $noGlow={!isModalOpen}
      $isFlashing={isCreateGroupButtonFlashing}
      $flashToColor={addOpacity(activeBackgroundColor, 0.7)}
      $backgroundColor={
        isModalOpen ? activeBackgroundColor : inactiveBackgroundColor
      }
    >
      <InnerWrapper>
        <IconWrapper
          $paddingRight={showButtonText ? iconPaddingRight : 0}
          $isSearchActive={!showButtonText}
        >
          <FnrContainedIcon
            icon="plus-circle fas"
            size="sm"
            color="textLight"
            backgroundColor="transparent"
            noPadding
            noAnimation
            shadow
          />
        </IconWrapper>
        <FnrSimpleReveal show={showButtonText}>
          <TextWrapper>
            <FnrTypography
              fontSize="13px"
              lineHeight={1}
              color="textLight"
              shadow
              noWrap
            >
              NEW
            </FnrTypography>
          </TextWrapper>
        </FnrSimpleReveal>
        <FnrSimpleReveal show={showButtonText && (isHovered || isFocusVisible)}>
          <FnrTypography
            fontSize="13px"
            lineHeight={1}
            color="textLight"
            shadow
            noWrap
          >
            GROUP
          </FnrTypography>
        </FnrSimpleReveal>
      </InnerWrapper>
    </FloatingButton>
  );
};

const TextWrapper = styled.div`
  padding-right: 4px;
`;

export default NewButton;
